/* eslint-disable no-template-curly-in-string */

import clsx from 'clsx'
import { Button, CardFileUploaded, Icon, Typography } from 'components'
import { CardAlert } from 'components/cadFileUploaded/cardAlert'
import useDragAndDrop from 'hooks/dragAndDrop/useDragAndDrop'
import { DocumentsInputs, Icons, Images, TypographyTypes } from 'interfaces'
import React from 'react'
import { configGenaral } from 'utils'

interface stepZeroProps {
	fileUploaded: boolean
	handleUploadCSV: (file: File) => void
	steps: string
	documentUpload: DocumentsInputs | undefined
	fileSize: number
	fileSizeUnit: string
	handleDeleteFile: () => void
}

export const StepZero: React.FC<stepZeroProps> = ({
	fileUploaded,
	handleUploadCSV,
	steps,
	documentUpload,
	fileSize,
	fileSizeUnit,
	handleDeleteFile
}) => {
	const typeAllowed = 'text/csv'
	const messageErrorFile = `Only .csv files are allowed`
	const { dragActive, handleDragOver, handleDragLeave, handleDrop } =
		useDragAndDrop(handleUploadCSV, typeAllowed, messageErrorFile)

	const handleNewRequest = (): void => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const win = window as any
		if (win?.zE) {
			win.zE.activate()
		}
	}

	const openFileUploader = (): void => {
		document.getElementById('fileInput')?.click()
	}
	return (
		<div className="flex flex-col justify-start w-full items-start gap-4">
			<div className="flex flex-col gap-2">
				<Typography
					title={!fileUploaded ? steps : 'Confirm Upload'}
					fontLeading="14/24"
					type={TypographyTypes.h1}
					className="text-black font-medium text-lg"
				/>
				<Typography
					title={
						!fileUploaded
							? 'Please download and fill out the spreadsheet template. This will help you ensure all necessary information is correctly formatted for upload.'
							: 'Your populated spreadsheet template has been uploaded.'
					}
					fontLeading="14/24"
					type={TypographyTypes.p}
					className="text-black font-normal text-base"
				/>

				{!fileUploaded && (
					<>
						<div className="flex justify-between items-center border-1 border-gray-1 rounded-md w-full py-4 px-8 bg-[#EBF9FA]">
							<div className="flex gap-2 items-center">
								<img src={Images.csv} alt="csv" className="w-9 h-9" />
								<Typography
									title="UsersTemplateFile.csv"
									fontLeading="14/24"
									type={TypographyTypes.p}
									className="text-black-1 font-normal text-base"
								/>
							</div>
							<Button
								label="Download Template"
								className="!min-w-[11.7rem]"
								color="white"
								iconRight={Icons.download}
								onClick={() => {
									const csvUrlDownload =
										'https://d2i1zdrrhxv4em.cloudfront.net/email_template/csv_example.csv'
									const link = document.createElement('a')
									link.href = csvUrlDownload
									link.download = 'csv_example.csv'
									document.body.appendChild(link)
									link.click()
									document.body.removeChild(link)
								}}
							/>
						</div>
						<Typography
							title="Upload your CSV file"
							fontLeading="14/24"
							type={TypographyTypes.h1}
							className="text-black font-medium text-lg pt-3"
						/>
						<Typography
							title="Please upload the populated spreadsheet template. Once the process is complete, 
							existing users will be updated and new ones created based on the information provided."
							fontLeading="14/24"
							type={TypographyTypes.p}
							className="text-black font-normal text-base"
						/>
					</>
				)}
			</div>
			{!fileUploaded && (
				<div
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={clsx(
						'flex flex-col gap-3 items-center justify-center border-2 border-dashed rounded-md w-full py-4 border-blue-primary',
						dragActive ? 'bg-[#cff8fb]' : 'bg-[#EBF9FA]'
					)}
				>
					<Icon
						src={Icons.cloudAdd}
						fillPath
						className="text-black-1 w-6 h-6"
					/>
					<Typography
						title="Choose a file or drag & drop it here"
						fontLeading="14/24"
						type={TypographyTypes.p}
						className="text-black-1 font-normal text-base"
					/>
					<Typography
						title={`CSV formats, up to ${configGenaral.maxFileSizeMBImportUser}MB`}
						fontLeading="14/24"
						type={TypographyTypes.p}
						className="text-gray-1 font-normal text-base"
					/>
					<Button
						label="Browse files"
						className="!min-w-[7rem]"
						color="white"
						onClick={openFileUploader}
						iconRight={Icons.cloudUploads}
						iconClassName="!w-10 !h-10"
					/>
					<input
						type="file"
						id="fileInput"
						accept=".csv"
						className="hidden"
						style={{ display: 'none' }}
						onChange={event => {
							if (event.target.files) {
								handleUploadCSV(event.target.files[0])
							}
						}}
					/>
				</div>
			)}
			{fileUploaded && (
				<>
					<CardFileUploaded
						nameFile={documentUpload?.name_file || ''}
						size={fileSize}
						unit={fileSizeUnit}
						onDelete={handleDeleteFile}
					/>
					<CardAlert
						title="Important!"
						description="Please note that only one import or update process via CSV can occur at a time."
						icon={Icons.breakingNews}
					/>
				</>
			)}

			<p className="text-black font-normal text-base mb-4">
				If you have any questions, you can contact support by clicking{' '}
				<button
					onClick={handleNewRequest}
					type="button"
					className="text-blue-primary underline"
				>
					here
				</button>
				.
			</p>
		</div>
	)
}
